import { useState } from 'react';

import { ButtonVariant, FlexBox, FlexItem, TrilogyColor } from '@bytel/trilogy-react';

import { filterPromotionsByTypesMarketing } from '@services/products';

import { DiscountType } from '@app-types/discount';
import { FaiType } from '@app-types/plan';

import { FaiOfferDetailsModal } from '@components/fai-offer-details-modal';
import { PromoHomeSticker } from '@components/promo-home-sticker';
import { NuggetsList } from '@components/roc-card';

import { calculateFaiPrice } from '@helpers/fai';
import { getSpeedInfos } from '@helpers/speed-infos';
import { capitalize } from '@helpers/wording';

import { MarketingType } from '@constants/plan';
import { PLAY } from '@constants/provider';

import { PlanConfigurator } from './plan-configurator';

interface FaiCardProps {
    plans: FaiType[];
    selectedPlan: FaiType;
    onSelectedPlan: (plan: FaiType) => void;
}

export function FaiCard({ plans, selectedPlan, onSelectedPlan }: Readonly<FaiCardProps>) {
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const onDetailsModalClose = () => {
        setIsDetailsModalOpen(false);
    };

    const initialPrice = calculateFaiPrice(selectedPlan);

    const optionsMapper = (plan: FaiType) => {
        let label;
        const speedInfos = getSpeedInfos(plan);
        const tv = plan.play === PLAY._3P ? ' + TV' : '';

        switch (plan.rangeNg) {
            case 'fit': {
                label = 'Fibre rapide' + tv;
                break;
            }
            case 'must': {
                label = 'Fibre très rapide' + tv;
                break;
            }
            case 'ultym': {
                label = 'Fibre ultra-rapide' + tv;
                break;
            }
            default: {
                label = plan.name;
            }
        }

        return {
            id: plan.gencode,
            onOptionChange: () => onSelectedPlan(plan),
            label,
            description: `↓\u00A0${speedInfos.downRates} ↑\u00A0${speedInfos.upRates} ${!!speedInfos.speedWording ? speedInfos.speedWording : ''}`,
        };
    };

    let discounts: DiscountType[] = [];
    if (selectedPlan.details.discounts) {
        discounts = filterPromotionsByTypesMarketing(selectedPlan.details.discounts, [MarketingType.HOME], false, true);
    }

    const options = plans.toSorted(plansSorter).map(optionsMapper);

    return (
        <FlexBox data-cy='fai-list'>
            <FlexItem
                size={{
                    mobile: 12,
                    tablet: 6,
                }}
                data-cy='fai-plan_0'
            >
                <PlanConfigurator
                    title={selectedPlan.name}
                    subtitle={capitalize(selectedPlan.obligationLabel)}
                    image={selectedPlan.image}
                    price={initialPrice}
                    sticker={<PromoHomeSticker promo={discounts[0]} small={false} />}
                    options={options}
                    defaultSelectedOptions={selectedPlan.gencode}
                    fullHeight={false}
                    nuggets={
                        <FlexItem>
                            <NuggetsList
                                plan={selectedPlan}
                                displaySpeed={!options}
                                boxBackground={TrilogyColor.NEUTRAL_FADE}
                            />
                        </FlexItem>
                    }
                    buttons={[
                        {
                            children: 'Voir les détails',
                            variant: ButtonVariant.SECONDARY,
                            onClick: () => setIsDetailsModalOpen(true),
                        },
                    ]}
                />
                {selectedPlan && (
                    <FaiOfferDetailsModal
                        plan={selectedPlan}
                        isOpen={isDetailsModalOpen}
                        onClose={onDetailsModalClose}
                    />
                )}
            </FlexItem>
        </FlexBox>
    );
}

function plansSorter(a: FaiType, b: FaiType) {
    const order = ['fit', 'must', 'ultym'];
    return order.indexOf(a.rangeNg) - order.indexOf(b.rangeNg);
}
